/**
 * The breakthrough ladder: what a candidate synthesis has EARNED.
 *
 * "Breakthrough" was a rank. The scan asked the model to rate each pair it had
 * just proposed, the tab sorted on that number, the `join` cue picked the
 * highest, and the exports printed it beside the title — so the one word this
 * app exists to make mean something was, everywhere it appeared, the proposing
 * model's opinion of its own proposal. This module replaces the rank with a
 * STATE. A candidate stands on a rung, every rung is a predicate over records
 * the model did not write on its own behalf, and nothing in this file reads
 * `score`. AGENTS.md invariant 10 holds it to that.
 *
 * The seven rungs, in the order they are climbed:
 *
 *   proposed        The pair exists. Every candidate starts here.
 *   searched        A link scan went looking for the conjunction and did not
 *                   find it — `unjoined`, from `core/link.ts` — and the index
 *                   scan, where one ran, did not find it either. A pair the
 *                   literature has already joined STOPS here instead, as
 *                   `already-made` or `prior-art`: a synthesis of published
 *                   work is not a candidate for anything.
 *   realised        The synthesis was written and is on the graph.
 *   attacked        A challenge or a panel has been run on it: a contradiction
 *                   stands under it, or three lenses have read it.
 *   cross-examined  A model from another house examined it and could not
 *                   object — `audit.verdict === 'pass'`, still live.
 *   checked         A source check landed `supported` and is still live. The
 *                   one reading on this ladder that passed the citation gate.
 *   observed        Somebody recorded an outcome that supported it.
 *
 * CONTIGUOUS. A candidate stands on the highest rung whose every lower rung
 * also holds, so a synthesis with a supporting source check that nobody ever
 * challenged stands at `realised`, not `checked`. That is a decision with a
 * cost: `channelOf` says an observation outranks every other field, and here
 * an observation on an unattacked synthesis does not lift it past the attack
 * it never had. The reason is what the ladder is FOR. Each rung is more
 * independent of the proposing model than the one below — its own challenge,
 * then another house, then the web behind the gate, then the world — and a
 * candidate that skipped one has a gap a reader should see before trusting
 * the rung above it. `earned` lists every rung that holds, contiguous or not,
 * so the gap is printed rather than hidden. D82 has the argument.
 *
 * A STOP ends a candidate; a HOLD keeps it where it is. `refuted` (a live
 * refuting check, or a refuting outcome, on the synthesis), `already-made` and
 * `prior-art` (the pair was joined before anyone here proposed it) and `closed`
 * (the synthesis was reasoned to a dead end) are stops, and a stopped candidate
 * sorts below every live one. `mixed` sources, an `inconclusive` outcome or a
 * standing objection are holds: the instrument ran and did not say yes, `held`
 * says so in a sentence, and the next press can still move it.
 *
 * Computed on every read and never stored, like `channelOf`: every record it
 * reads is already on the bridge or the node, so a stored rung would be one
 * more copy that could drift from what it summarises. The list is sorted by it
 * where it is RENDERED, and `persist.ts` sorts by it once on the way in, only
 * to decide which candidates survive `MAX_BRIDGES`.
 *
 * Imports `./types` and nothing else at runtime, so the store, the codec and
 * the UI can all read it without joining a cycle.
 */

import {
  BRIDGE_RUNGS,
  auditLive,
  bridgeReadable,
  demotedForFailedSearch,
  endKey,
  localEnds,
  outcomeLive,
  stanceTally,
  verificationLive,
  type Bridge,
  type BridgeRung,
  type ReasonNode,
  type SourceReading,
  type Verification,
  panelLive,
} from './types'
import type { LinkReading } from './link'
import { CHANNEL_WORD } from './vocabulary'

export type BridgeStop = 'refuted' | 'already-made' | 'prior-art' | 'closed'

export interface BridgeStanding {
  /** The highest rung whose every lower rung also holds. */
  rung: BridgeRung
  /** `BRIDGE_RUNGS.indexOf(rung)`, for the sort and the ladder drawing. */
  index: number
  /** Every rung whose predicate holds, in ladder order, gaps included. */
  earned: BridgeRung[]
  /** What ended it, or null while it can still climb. */
  stop: BridgeStop | null
  /** The rung above `rung`, or null at the top or once stopped. */
  next: BridgeRung | null
  /** Why `next` is not earned although its instrument has RUN, or null when the
   *  instrument has simply not been run yet. The difference is the difference
   *  between "press this" and "this was pressed and did not say yes". */
  held: string | null
  /** The realised synthesis, when it is on the graph. */
  nodeId: string | null
  /** When `rung` was reached, read off the record that earned it. */
  reachedAt: number
}

/** Children by parent id, built once per graph and handed to every candidate. */
export function childrenOf(nodes: Record<string, ReasonNode>): Map<string, string[]> {
  const kids = new Map<string, string[]>()
  for (const n of Object.values(nodes)) {
    if (n.parentId === null) continue
    const list = kids.get(n.parentId)
    if (list) list.push(n.id)
    else kids.set(n.parentId, [n.id])
  }
  return kids
}

/** The link readings that neither earn `searched` nor stop the candidate, and
 *  what each means for the person deciding whether to scan again. */
const LINK_HOLD: Record<Exclude<LinkReading, 'already-made' | 'unjoined'>, string> = {
  unsound: 'the link scan errored partway, so its finding nothing proves nothing',
  thin: 'the link scan found too little on one side to search for the conjunction',
  'no-intermediate': 'the two literatures share no term a mechanism could run through',
}

/** The source readings that neither earn `checked` nor stop the candidate.
 *  Mixed and unsourced ones are said from their records instead, by `mixedHold`
 *  and `unsourcedHold`. */
const READING_HOLD: Record<
  Exclude<SourceReading, 'supported' | 'refuted' | 'mixed' | 'unsourced'>,
  string
> = {
  'nothing-found': 'the source check found nothing either way',
  'not-checkable': 'sources cannot settle a claim of this kind',
}

/**
 * Why a `not-checkable` the gate wrote holds the candidate. The table's clause is
 * the pass's word, that sources cannot settle a claim of this kind, and here the
 * pass gave an absence, or no reading at all, beside a search that partly failed:
 * the claim may be one sources settle, and this check could not say. In
 * `readingNote`'s terms (D326).
 */
const FAILED_SEARCH_HOLD =
  "some of the source check's searches did not complete, so it could not say either way"

/**
 * Why an unsourced check holds the candidate, in the three cases `readingNote`
 * tells apart, and in its words.
 *
 * One fixed clause said "every source the check named was fabricated and was
 * deleted" under all of them, which says the check named something. A check is
 * also unsourced when it named nothing: no source the search returned, or, on a
 * documents-only check, no passage. Where it did name some, the count is the
 * gate's own, the one the note prints, and "set aside" is the note's word for what
 * happened to them, because the gate knows a URL was not returned, not that it
 * was made up (D322).
 */
function unsourcedHold(v: Verification): string {
  if (v.scope === 'corpus') return 'the source check named no passage from your documents'
  const n = v.dropped
  if (n === 0) return 'the source check named no source the search returned'
  return `the source check named ${n} source${n === 1 ? '' : 's'}, but none ${n === 1 ? 'was' : 'were'} in what the search returned, so ${n === 1 ? 'it was' : `all ${n} were`} set aside`
}

/**
 * Why a mixed check holds the candidate: which way the sources that take a side
 * went, in the four shapes `readingNote` tells apart, read off the same count.
 *
 * One fixed clause said "the sources are mixed — some support it and some
 * dispute it" under every mixed check, and three of the four shapes make it
 * false. Every refutation the gate demotes lands as mixed, live in `reconcile`
 * and on the way off disk in `asVerification`, and one can have no supporting
 * source at all. The verify prompt sends a check with no dispute here too:
 * something else made it mixed, and the summary says what. And on a check of the
 * documents alone no source takes a side, because a passage carries no stance.
 * There the clause says what the reading does and no more, in the opening words
 * of `READING_NOTE.mixed`.
 *
 * No counts. The shape is the reason `checked` was not earned; the counts are in
 * the strip on the synthesis's own check, behind "Go to the synthesis", and after
 * "Held below checked:" they read badly (D323).
 */
function mixedHold(v: Verification): string {
  const { supports, disputes } = stanceTally(v.sources)
  if (disputes > 0 && supports === 0) {
    return disputes === 1
      ? 'the source that takes a side disputes it'
      : 'the sources that take a side dispute it'
  }
  if (supports > 0 && disputes === 0) {
    const sided =
      supports === 1
        ? 'the source that takes a side supports it'
        : 'the sources that take a side support it'
    return `${sided}, and the check’s summary says what else made the reading mixed`
  }
  if (supports > 0 && disputes > 0) return 'the sources that take a side disagree with each other'
  return 'the sources do not line up cleanly'
}

/**
 * Where one candidate stands, read off its records.
 *
 * Pure, and reads nothing but the bridge, the nodes and the children map. The
 * order of the stops when several apply is by authority: the world, then the
 * gate, then the literature, then the model's own dead end — so a refuted
 * synthesis of published work reads as refuted, which is the stronger fact.
 */
/**
 * Whether this node is the synthesis of THIS candidate, rather than a node the
 * pointer merely names. See the note at the call site.
 */
function realisedBy(n: ReasonNode, b: Bridge): boolean {
  if (n.kind !== 'synthesis' && n.kind !== 'insight') return false
  return [b.a, b.b].every((e) =>
    e.where === 'local'
      ? // The near end is the synthesis's parent, and both ends are in
        // `bridgeFrom` when both are local.
        n.parentId === e.nodeId || (n.bridgeFrom?.includes(e.nodeId) ?? false)
      : // And the FAR one is `foreignFrom`, which `realiseBridge` writes in place
        // of `bridgeFrom` when the partner is off this canvas. It went unchecked
        // for one round on the reasoning that a foreign end has no id here — but
        // it carries its own, which is the whole point of the field. Without
        // this, any synthesis under the near node passed: one realised from a
        // DIFFERENT exploration, or from no candidate at all. `endKey` is the
        // identity a pair is already keyed by.
        !!n.foreignFrom && endKey({ where: 'foreign', ref: n.foreignFrom }) === endKey(e),
  )
}

/**
 * Whether a link scan's reading still describes the pair it searched.
 *
 * A TIMESTAMP, where `outcomeLive` refuses one — the two records are not alike
 * and the difference is which of them is a reading of prose. An observation is
 * about the world and survives a rewrite; a link scan is a search built from the
 * two claims' own terms, and `StoredLinkage.queries` exists so that `unjoined`
 * can be audited against what was looked for. Reword an endpoint and the absence
 * was measured against terms that are gone. That is `verificationLive`'s
 * situation exactly, and this is `verificationLive`'s fallback, because
 * `StoredLinkage` carries no wording of its own to compare — it has `scannedAt`
 * and `model` and nothing that names the propositions.
 *
 * LOCAL ends only. A foreign end is a `ForeignRef` snapshot taken at a moment
 * this document records; it cannot change underneath the scan, so asking about
 * it would be asking about a copy nobody can edit.
 *
 * An end whose node has gone does not make the scan stale — that is a dangling
 * candidate, which `bridgeReadable` answers, and two questions answered in one
 * place is how the wrong one gets used.
 *
 * The cost is the ladder's contiguity: a candidate that had climbed to `checked`
 * reads `proposed` after an endpoint is reworded, because nothing above
 * `searched` is reachable without it. That is the same cost a stale check
 * already carries on the rung below, accepted for the same reason D82 gives, and
 * it is not a dead end — the card's own search button re-runs the scan through
 * `runLinkScan(b.id)`, which patches the linkage in place.
 */
function linkageLive(b: Bridge, nodes: Record<string, ReasonNode>): boolean {
  const scannedAt = b.linkage?.scannedAt
  if (scannedAt === undefined) return false
  return localEnds(b).every((id) => {
    const editedAt = nodes[id]?.editedAt
    return editedAt === undefined || editedAt <= scannedAt
  })
}

export function bridgeStanding(
  b: Bridge,
  nodes: Record<string, ReasonNode>,
  kids: ReadonlyMap<string, string[]> = childrenOf(nodes),
): BridgeStanding {
  const earned: BridgeRung[] = ['proposed']
  const at: Partial<Record<BridgeRung, number>> = { proposed: b.createdAt }
  const holds: Partial<Record<BridgeRung, string>> = {}
  const stops: BridgeStop[] = []

  // `linkageLive`, for the reason a stale verification is skipped further down:
  // a link scan is a CHECK — run by the check role, priced as one — and its
  // reading is about the two propositions it searched. The queries are stored
  // precisely so an absence means something next to what was looked for, and a
  // rewritten endpoint is an absence measured against terms that are gone.
  const l = b.linkage && linkageLive(b, nodes) ? b.linkage : undefined
  if (l) {
    if (l.reading === 'already-made') stops.push('already-made')
    else if (l.index?.reading === 'already-made') stops.push('prior-art')
    else if (l.reading === 'unjoined') {
      earned.push('searched')
      at.searched = l.scannedAt
    } else holds.searched = LINK_HOLD[l.reading]
  }

  // A dangling `realizedNodeId` is a synthesis that was pruned, and it reads as
  // not realised rather than as a stop: undo brings the node back, and a
  // candidate that could be realised again is not finished.
  //
  // And a POINTER IS NOT A REALISATION. Every rung above `realised` is earned on
  // this node, so a `realizedNodeId` naming any node that happens to exist hands
  // the candidate that node's contradiction, audit, check and outcome — the
  // whole ladder through `observed`, on a synthesis nobody wrote. `asBridge`
  // checks the id's shape and not its meaning, and a merged snapshot can name
  // one.
  //
  // The test is how `realiseBridge` actually writes one: the synthesis is
  // parented on the near end and carries `bridgeFrom` naming both when the far
  // end is local, and its kind is forced to `synthesis` or `insight`. So it must
  // be attached to every LOCAL end this candidate has — a foreign partner has no
  // id on this canvas and is not one. Failing that it reads as not realised,
  // which is what a dangling pointer already reads as.
  const claimed = b.realizedNodeId ? nodes[b.realizedNodeId] : undefined
  const node = claimed && realisedBy(claimed, b) ? claimed : undefined
  if (node) {
    earned.push('realised')
    at.realised = node.createdAt
    if (node.terminal && node.terminalKind !== 'constraint') stops.push('closed')

    // An attack has to post-date the last rewrite, or it argues with wording the
    // proposition no longer carries — and this rung is what orders the tab, the
    // cue and every export. `panelLive` is the rule for that question, and its
    // own header names THIS reader as the one that once awarded `attacked` off a
    // panel every other reader already knew was stale.
    const attacks: number[] = []
    if (node.panel && panelLive(node.panel.checkedAt, node.editedAt)) {
      attacks.push(node.panel.checkedAt)
    }
    for (const id of kids.get(node.id) ?? []) {
      const k = nodes[id]
      // NOT gated on the rewrite, and the asymmetry with the panel above is the
      // point. A panel is a RECORD on the node — three lenses ruling on wording
      // that is gone, which `reviseNode` clears in the same patch as `editedAt`.
      // A contradiction is a child NODE: it carries its own claim, it is on the
      // canvas where a reader can open it and judge whether it still bites, and
      // nothing anywhere retires it. `attacked` says somebody challenged this
      // node, and somebody did.
      //
      // The cost of the other answer is what settles it. The ladder is
      // contiguous (D82), so retiring the challenge retires everything above it
      // — a synthesis that had been cross-examined, checked and observed would
      // fall to `realised` because a typo was fixed in its claim, and the tab,
      // the cue and every export reorder on that.
      if (k && k.kind === 'contradiction') attacks.push(k.createdAt)
    }
    if (attacks.length) {
      earned.push('attacked')
      at.attacked = Math.min(...attacks)
    }

    if (node.audit && auditLive(node)) {
      if (node.audit.verdict === 'pass') {
        earned.push('cross-examined')
        at['cross-examined'] = node.audit.at
      } else
        holds['cross-examined'] = 'a model from another house objected, and the objection stands'
    }

    const v = node.verification
    if (v && verificationLive(node)) {
      if (v.reading === 'refuted') stops.push('refuted')
      else if (v.reading === 'supported') {
        earned.push('checked')
        at.checked = v.checkedAt
      } else if (v.reading === 'mixed') holds.checked = mixedHold(v)
      else if (v.reading === 'unsourced') holds.checked = unsourcedHold(v)
      else if (demotedForFailedSearch(v)) holds.checked = FAILED_SEARCH_HOLD
      else holds.checked = READING_HOLD[v.reading]
    }

    // `outcomeLive`, for the reason the two records above are read through their
    // own predicates: an observation survives a rewrite of the claim it was
    // recorded against — it really happened — but it describes the proposition
    // named in `claimAtRecord`, not whatever replaced it. A field-wise merge
    // pairs one branch's rewritten synthesis with the other's outcome and raises
    // no conflict, and a local rewrite leaves the record standing too, so this is
    // reachable on one machine as well as through a merge.
    //
    // Not consulted at all when stale, which is exactly what a stale
    // verification gets three lines up: no rung, no stop, and no hold sentence.
    // The record is still on the node for a reader to open; what it must not do
    // is move a candidate up the ladder or STOP it, on words that are gone.
    if (node.outcome && outcomeLive(node)) {
      if (node.outcome.bearing === 'refuted') stops.push('refuted')
      else if (node.outcome.bearing === 'supported') {
        earned.push('observed')
        at.observed = node.outcome.recordedAt
      } else holds.observed = 'the recorded outcome was inconclusive'
    }
  }

  let rung: BridgeRung = 'proposed'
  for (const r of BRIDGE_RUNGS) {
    if (earned.includes(r)) rung = r
    else break
  }
  const index = BRIDGE_RUNGS.indexOf(rung)
  const stop = STOP_ORDER.find((s) => stops.includes(s)) ?? null
  const next = stop ? null : (BRIDGE_RUNGS[index + 1] ?? null)
  return {
    rung,
    index,
    earned,
    stop,
    next,
    held: next ? (holds[next] ?? null) : null,
    nodeId: node?.id ?? null,
    reachedAt: at[rung] ?? b.createdAt,
  }
}

/** Authority order for the stops — see `bridgeStanding`. */
const STOP_ORDER: readonly BridgeStop[] = ['refuted', 'already-made', 'prior-art', 'closed']

/**
 * The list in the order the tab, the cue and the account read it.
 *
 * Live candidates before stopped ones; a higher rung before a lower; more rungs
 * earned before fewer within a rung; the most recently reached first after
 * that, so the candidate that just moved is at the top; then newest proposed.
 * `score` appears nowhere in this comparison, and the test that holds invariant
 * 10 reads this file for the word.
 */
export function sortBridges(
  bridges: readonly Bridge[],
  nodes: Record<string, ReasonNode>,
): Bridge[] {
  const kids = childrenOf(nodes)
  const standing = new Map(bridges.map((b) => [b.id, bridgeStanding(b, nodes, kids)] as const))
  return [...bridges].sort((p, q) => {
    const a = standing.get(p.id)!
    const z = standing.get(q.id)!
    return (
      Number(a.stop !== null) - Number(z.stop !== null) ||
      z.index - a.index ||
      // Within a rung, the candidate with more rungs earned above the gap: an
      // unsearched pair that was realised has more on record than one that was
      // not, and the ladder says so even while both stand at proposed.
      z.earned.length - a.earned.length ||
      z.reachedAt - a.reachedAt ||
      q.createdAt - p.createdAt
    )
  })
}

/**
 * The pair the `join` cue is about: the first in ladder order that no link scan
 * has searched, whose ends are both still readable, and that has this claim as a
 * local end. Resolved from the document at the press, never carried on the cue,
 * because a cue is JSON and a candidate frozen into one could name a pair a prune
 * has since made unreadable. One definition for every door the cue has: the
 * coach's button, and the MCP server's suggested call (D317).
 */
export function joinCandidate(
  s: { bridges: readonly Bridge[]; nodes: Record<string, ReasonNode> },
  anchorId: string,
): Bridge | null {
  for (const b of sortBridges(s.bridges, s.nodes)) {
    if (b.linkage || !bridgeReadable(b, s.nodes)) continue
    if (localEnds(b).includes(anchorId)) return b
  }
  return null
}

/** How many candidates stand on each rung, and how many have stopped. For the
 *  report, the manifest and the tab's legend. */
export function rungCounts(
  bridges: readonly Bridge[],
  nodes: Record<string, ReasonNode>,
): { byRung: Record<BridgeRung, number>; stopped: number } {
  const kids = childrenOf(nodes)
  const byRung = Object.fromEntries(BRIDGE_RUNGS.map((r) => [r, 0])) as Record<BridgeRung, number>
  let stopped = 0
  for (const b of bridges) {
    const s = bridgeStanding(b, nodes, kids)
    if (s.stop) stopped++
    else byRung[s.rung]++
  }
  return { byRung, stopped }
}

export const RUNG_LABEL: Record<BridgeRung, string> = {
  proposed: 'Proposed',
  searched: 'Searched',
  realised: 'Realised',
  attacked: 'Attacked',
  'cross-examined': 'Cross-examined',
  checked: 'Checked',
  observed: 'Observed',
}

/**
 * Each rung's predicate, in one sentence, printed on the card beside the rung.
 *
 * The same contract `core/cues.ts` keeps for its WHY line: a rung that cannot
 * say what earned it is a verdict handed down, and the ladder exists to be the
 * opposite of one.
 */
export const RUNG_RULE: Record<BridgeRung, string> = {
  proposed: 'The pair was proposed. Nothing has been checked.',
  // "found no prior art either", never "agreed". The predicate is that the index
  // did not return `already-made` — which `thin`, `no-intermediate`, `unsound`
  // and no index scan at all all satisfy, none of them by agreeing with
  // anything. A rung may not claim more corroboration than earned it; that is
  // the whole of what this record is for. The header above had it right.
  searched:
    'A link scan searched for the conjunction and did not find it, and the index scan, where one ran, found no prior art either.',
  realised: 'The synthesis was written and is on the graph.',
  attacked: 'A challenge stands under the synthesis, or a panel has read it.',
  'cross-examined':
    'A model from another provider examined the synthesis and could not object, and it has not been rewritten since.',
  checked: 'A source check landed supported on the synthesis, and it has not been rewritten since.',
  observed: 'An outcome was recorded against the synthesis and it supported the claim.',
}

export const STOP_LABEL: Record<BridgeStop, string> = {
  refuted: 'Refuted',
  'already-made': 'Already made',
  'prior-art': 'Prior art',
  closed: CHANNEL_WORD.dead,
}

export const STOP_RULE: Record<BridgeStop, string> = {
  refuted: 'A source check or a recorded outcome refuted the synthesis.',
  'already-made': 'The link scan found pages that already join the two sides.',
  'prior-art': 'The index scan found papers that already join the two sides.',
  closed: 'The synthesis was reasoned to a dead end.',
}

/**
 * The press that climbs from a rung to the one above, as DATA — the same rule
 * `CueAction` and `Promote` keep (AGENTS.md invariant 9). Nothing here can run a
 * pass; the tab reads the descriptor and dispatches when somebody presses.
 */
export type RungActionKind = 'linkScan' | 'realise' | 'challenge' | 'audit' | 'verify' | 'record'

export interface RungAction {
  kind: RungActionKind
  label: string
}

export const RUNG_ACTION: Record<BridgeRung, RungAction | null> = {
  proposed: { kind: 'linkScan', label: 'Search for prior art' },
  searched: { kind: 'realise', label: 'Realise the synthesis' },
  realised: { kind: 'challenge', label: 'Challenge it' },
  attacked: { kind: 'audit', label: 'Cross-examine it' },
  'cross-examined': { kind: 'verify', label: 'Check it against sources' },
  checked: { kind: 'record', label: 'Record what happened' },
  observed: null,
}

/** Which presses spend. Written out for the reason `CUE_ACTION_PAID` is: a
 *  press added later must not inherit the wrong answer by omission. */
export const RUNG_ACTION_PAID: Record<RungActionKind, boolean> = {
  linkScan: true,
  realise: true,
  challenge: true,
  audit: true,
  verify: true,
  record: false,
}

/** The height of the ladder, for the "n of 7" every export prints. */
export const BRIDGE_RUNGS_TOTAL = BRIDGE_RUNGS.length
